import React from 'react';
import { ActionType, ActionState } from '../constants';
import {
  ActionTypeRegistry,
  ActionTypeDefinition,
  CardContentProps,
  RejectionModalContentProps,
  AcceptModalContentProps,
  EditModalContentProps,
  ActionCommandType,
  CommandConfig,
} from '../types';
import { MODAL_TYPES } from '../../../components/modals/constants';
import { capitalize } from '@utils/utils';

// Import existing components
import {
  ScheduleAppointmentCard,
  GenerateClaimCard,
  PepCCard,
  TemplateCleanupCard,
  LabOrderSuggestionCard,
  PatientUpdateCard,
} from '../components/cards';
import {
  ScheduleAppointmentRejection,
  GenerateClaimRejection,
  PepCRejection,
  TemplateCleanupRejection,
  LabOrderSuggestionRejection,
  PatientUpdateRejection,
} from '../components/rejection';
import { GenerateClaimAcceptance } from '../components/acceptance';
import { PepCEditModal } from '../components/edit';

/**
 * Unified registry mapping action types to their complete definitions
 * This replaces the old rejectionModalRegistry and provides a single source of truth
 * for all action-type-specific components and configurations
 */
const actionTypeRegistry: ActionTypeRegistry = {
  [ActionType.SCHEDULE_APPOINTMENT]: {
    cardContent: ScheduleAppointmentCard as React.ComponentType<CardContentProps>,
    rejectionModal: ScheduleAppointmentRejection,
    // No acceptModal for appointments - they accept immediately or after inline editing
    config: {
      allowsInlineEditing: true,
      commands: {
        [ActionCommandType.EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.SAVE_EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT_WITH_EDITS]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
      },
    },
  },

  [ActionType.GENERATE_CLAIM]: {
    cardContent: GenerateClaimCard as React.ComponentType<CardContentProps>,
    rejectionModal: GenerateClaimRejection,
    acceptModal: GenerateClaimAcceptance,
    config: {
      allowsInlineEditing: true, // Allow inline editing of codes
      commands: {
        [ActionCommandType.EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.SAVE_EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: true, // Generate claim always requires confirmation
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT_WITH_EDITS]: {
          requiresConfirmation: true, // Generate claim always requires confirmation
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
      },
    },
  },

  [ActionType.PEP_C]: {
    cardContent: PepCCard as React.ComponentType<CardContentProps>,
    rejectionModal: PepCRejection,
    editModal: PepCEditModal,
    config: {
      allowsInlineEditing: false, // PEP-C only uses modal editing
      allowsModalEditing: true,
      requiresOnboarding: [
        {
          onboardingKey: 'patient_communication_sms_onboarding_completed',
          modalType: MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING,
        },
      ],
      validation: {
        validate: action => {
          const missingFields: string[] = [];
          const missingFieldLabels: string[] = [];

          // Check for required patient information
          if (!action.patient?.telephone) {
            missingFields.push('phone');
            missingFieldLabels.push('phone number');
          }
          if (!action.patient?.dob) {
            missingFields.push('dob');
            missingFieldLabels.push('date of birth');
          }

          // Build dynamic error message
          let errorMessage: string | undefined;
          if (missingFieldLabels.length > 0) {
            const fieldsText =
              missingFieldLabels.length === 1
                ? missingFieldLabels[0]
                : missingFieldLabels.length === 2
                  ? `${missingFieldLabels[0]} and ${missingFieldLabels[1]}`
                  : missingFieldLabels.slice(0, -1).join(', ') +
                    ', and ' +
                    missingFieldLabels[missingFieldLabels.length - 1];

            errorMessage = `${capitalize(fieldsText)} ${missingFieldLabels.length === 1 ? 'is' : 'are'} required to send the summary`;
          }

          return {
            isValid: missingFields.length === 0,
            missingFields,
            errorMessage,
          };
        },
        validationModal: MODAL_TYPES.PATIENT_INFO_COLLECTION,
      },
      commands: {
        [ActionCommandType.EDIT_IN_MODAL]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: false, // Direct send to patient
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT_WITH_EDITS]: {
          requiresConfirmation: false, // Direct send after editing
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
      },
    },
  },

  [ActionType.TEMPLATE_CLEANUP]: {
    cardContent: TemplateCleanupCard as React.ComponentType<CardContentProps>,
    rejectionModal: TemplateCleanupRejection,
    config: {
      allowsInlineEditing: true,
      commands: {
        [ActionCommandType.EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.SAVE_EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT_WITH_EDITS]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
      },
    },
  },

  [ActionType.LAB_ORDER_SUGGESTION]: {
    cardContent: LabOrderSuggestionCard as React.ComponentType<CardContentProps>,
    rejectionModal: LabOrderSuggestionRejection,
    config: {
      allowsInlineEditing: false, // Lab orders are edited in the modal
      commands: {
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: false, // Opens modal with auto-generate PDF
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
        [ActionCommandType.EDIT]: {
          allowedStates: [ActionState.SUGGESTED], // Opens modal for editing
        },
      },
    },
  },

  [ActionType.PATIENT_UPDATE]: {
    cardContent: PatientUpdateCard as React.ComponentType<CardContentProps>,
    rejectionModal: PatientUpdateRejection,
    config: {
      allowsInlineEditing: true, // Allow inline editing of patient field values
      commands: {
        [ActionCommandType.EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.SAVE_EDIT]: {
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.ACCEPT_WITH_EDITS]: {
          requiresConfirmation: false,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.REJECT]: {
          requiresConfirmation: true,
          allowedStates: [ActionState.SUGGESTED],
        },
        [ActionCommandType.EXPAND]: {},
        [ActionCommandType.COLLAPSE]: {},
      },
    },
  },
};

/**
 * Get the card content component for an action type
 * @param actionType - The action type to get content for
 * @returns React component for rendering card content
 */
export function getCardContent(actionType: ActionType): React.ComponentType<CardContentProps> {
  const definition = actionTypeRegistry[actionType];

  if (!definition) {
    console.warn(
      `No definition found for action type: ${actionType}, using schedule_appointment as fallback`,
    );
    return actionTypeRegistry[ActionType.SCHEDULE_APPOINTMENT].cardContent;
  }

  return definition.cardContent;
}

/**
 * Get the rejection modal content component for an action type
 * @param actionType - The action type to get content for
 * @returns React component for rendering rejection modal content, or null if not defined
 */
export function getRejectionModal(
  actionType: ActionType,
): React.ComponentType<RejectionModalContentProps> | null {
  const definition = actionTypeRegistry[actionType];

  if (!definition) {
    console.warn(
      `No definition found for action type: ${actionType}, no rejection modal available`,
    );
    return null;
  }

  return definition.rejectionModal || null;
}

/**
 * Get the accept modal content component for an action type
 * @param actionType - The action type to get content for
 * @returns React component for rendering accept modal content, or null if not defined
 */
export function getAcceptModal(
  actionType: ActionType,
): React.ComponentType<AcceptModalContentProps> | null {
  const definition = actionTypeRegistry[actionType];

  if (!definition) {
    console.warn(`No definition found for action type: ${actionType}, no accept modal available`);
    return null;
  }

  return definition.acceptModal || null;
}

/**
 * Get the edit modal content component for an action type
 * @param actionType - The action type to get content for
 * @returns React component for rendering edit modal content, or null if not defined
 */
export function getEditModal(
  actionType: ActionType,
): React.ComponentType<EditModalContentProps> | null {
  const definition = actionTypeRegistry[actionType];

  if (!definition) {
    console.warn(`No definition found for action type: ${actionType}, no edit modal available`);
    return null;
  }

  return definition.editModal || null;
}

/**
 * Check if an action type allows inline editing
 * @param actionType - The action type to check
 * @returns true if inline editing is allowed, false otherwise
 */
export function allowsInlineEditing(actionType: ActionType): boolean {
  const definition = actionTypeRegistry[actionType];
  return definition?.config?.allowsInlineEditing ?? false;
}

/**
 * Register a new action type definition
 * @param actionType - The action type to register
 * @param definition - The complete definition for this action type
 */
export function registerActionType(actionType: ActionType, definition: ActionTypeDefinition): void {
  actionTypeRegistry[actionType] = definition;
  console.log(`📝 Registered action type definition: ${actionType}`);
}

/**
 * Get all registered action types
 * @returns Array of action types that have registered definitions
 */
export function getRegisteredActionTypes(): ActionType[] {
  return Object.keys(actionTypeRegistry) as ActionType[];
}

/**
 * Get the complete definition for an action type
 * @param actionType - The action type to get definition for
 * @returns Complete action type definition or null if not found
 */
export function getActionTypeDefinition(actionType: ActionType): ActionTypeDefinition | null {
  return actionTypeRegistry[actionType] || null;
}

/**
 * Get command configuration for a specific action type and command
 * @param actionType - The action type
 * @param commandType - The command type
 * @returns Command configuration or null if not found
 */
export function getCommandConfig(
  actionType: ActionType,
  commandType: string,
): CommandConfig | null {
  const definition = actionTypeRegistry[actionType];
  return definition?.config?.commands?.[commandType] || null;
}

/**
 * Check if a command is allowed for an action in its current state
 * @param actionType - The action type
 * @param commandType - The command type
 * @param actionState - The current action state
 * @returns true if command is allowed, false otherwise
 */
export function isCommandAllowed(
  actionType: ActionType,
  commandType: string,
  actionState: string,
): boolean {
  const commandConfig = getCommandConfig(actionType, commandType);
  if (!commandConfig) return false;

  // If no allowed states specified, command is always allowed
  if (!commandConfig.allowedStates) return true;

  return commandConfig.allowedStates.includes(actionState as any);
}

/**
 * Check if a command requires confirmation
 * @param actionType - The action type
 * @param commandType - The command type
 * @returns true if confirmation is required, false otherwise
 */
export function requiresCommandConfirmation(actionType: ActionType, commandType: string): boolean {
  const commandConfig = getCommandConfig(actionType, commandType);
  return commandConfig?.requiresConfirmation ?? false;
}
